/**
 * PKCE material for the authorization code flow — RFC 7636.
 *
 * `ninetymobile://` is an unclaimed custom scheme, so another app on the device can register it and
 * receive the redirect. PKCE is the only reason that is acceptable: an intercepted code cannot be
 * redeemed without the verifier, which never leaves this process until it is sent straight to the
 * token endpoint.
 *
 * These are free functions rather than a service because they take no dependencies and hold no
 * state. The call site that matters is the `prepareAuthorizationRequest` handler, which generates the
 * material itself so no component is ever in a position to choose its own.
 */

/** RFC 7636 §4.1 permits 43–128 unreserved characters. 32 random bytes base64url-encode to 43. */
const VERIFIER_BYTES = 32;
const NONCE_BYTES = 16;

/**
 * base64url without padding — RFC 4648 §5.
 *
 * Plain base64 is wrong here in three ways at once: `+` becomes a space when a query string is
 * form-decoded, `/` is a path separator, and `=` padding is not in the unreserved set. Any of them
 * turns a valid verifier into one that no longer matches its own challenge, and the failure arrives
 * from Cognito as an opaque `invalid_grant`.
 */
function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomBase64Url(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** A fresh code verifier. Never logged, and never held anywhere but the grant. */
export function createCodeVerifier(): string {
  return randomBase64Url(VERIFIER_BYTES);
}

/**
 * A fresh `state` nonce.
 *
 * Separate from the verifier because they answer different questions: the verifier proves this
 * process redeemed the code, the nonce proves the redirect belongs to the request we sent. Reusing
 * one value for both would put the verifier in the redirect URL, where anything registered on the
 * scheme can read it.
 */
export function createNonce(): string {
  return randomBase64Url(NONCE_BYTES);
}

/**
 * The S256 challenge for a verifier.
 *
 * `S256` rather than `plain`: RFC 7636 §7.2 permits `plain` only where the client cannot do SHA-256,
 * and both a WebView and a browser can. With `plain` the verifier travels in the authorization URL,
 * which defeats the point on an unclaimed scheme.
 */
export async function createCodeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}
